import { ChangeDetectionStrategy, Component, computed, effect, forwardRef, inject, input, OnInit } from '@angular/core';
import { FormBuilder, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  CalendarEventRecurrence,
  CalendarRecurrenceDayOfWeek,
  CalendarRecurrencePatternType,
  CalendarRecurrenceWeekIndex,
} from '@cue/calendars';
import { translation } from '@cue/translations';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DropdownlistComponent } from '@cue/assist/ui/dropdowns';
import { DatepickerComponent } from '@assist/ui/dateinputs';
import { NgClass } from '@angular/common';
import { TextboxComponent } from '@cue/assist/ui/inputs';

type Frequency = 'never' | 'daily' | 'weekly' | 'monthly' | 'yearly';
type RangeType = 'never' | 'after' | 'on';
type MonthlyMode = 'absolute' | 'relative';
type YearlyMode = 'absolute' | 'relative';

type SelectOption<T extends string | number> = {
  label: string;
  value: T;
};

const DEFAULT_SUPPORTED_PATTERN_TYPES: CalendarRecurrencePatternType[] = [
  'daily',
  'weekly',
  'absoluteMonthly',
  'relativeMonthly',
  'absoluteYearly',
  'relativeYearly',
];

const DAYS_OF_WEEK: CalendarRecurrenceDayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
const WEEK_INDEXES: CalendarRecurrenceWeekIndex[] = ['first', 'second', 'third', 'fourth', 'last'];

@Component({
  selector: 'assist-recurrence-editor',
  standalone: true,
  templateUrl: './recurrence-editor.component.html',
  styleUrls: ['./recurrence-editor.component.scss'],
  imports: [ReactiveFormsModule, TranslocoPipe, DropdownlistComponent, DatepickerComponent, TextboxComponent, NgClass],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RecurrenceEditorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecurrenceEditorComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly translocoService = inject(TranslocoService);
  private suppressChange = false;

  translation = translation;
  start = input.required<Date>();
  maxEndDate = input<Date | undefined>();
  disabled = input(false);
  supportedPatternTypes = input<CalendarRecurrencePatternType[]>(DEFAULT_SUPPORTED_PATTERN_TYPES);

  recurrenceForm = this.fb.group({
    frequency: this.fb.nonNullable.control<Frequency>('never'),
    interval: this.fb.nonNullable.control(1),
    weeklyDays: this.fb.nonNullable.control<CalendarRecurrenceDayOfWeek[]>([]),
    monthlyMode: this.fb.nonNullable.control<MonthlyMode>('absolute'),
    yearlyMode: this.fb.nonNullable.control<YearlyMode>('absolute'),
    dayOfMonth: this.fb.nonNullable.control(1),
    month: this.fb.nonNullable.control(1),
    weekIndex: this.fb.nonNullable.control<CalendarRecurrenceWeekIndex>('first'),
    weekDay: this.fb.nonNullable.control<CalendarRecurrenceDayOfWeek>('monday'),
    rangeType: this.fb.nonNullable.control<RangeType>('on'),
    occurrences: this.fb.nonNullable.control(10),
    endDate: this.fb.control<Date | null>(null),
  });

  private readonly frequencyValue = toSignal(this.recurrenceForm.controls.frequency.valueChanges, {
    initialValue: this.recurrenceForm.controls.frequency.value,
  });

  private readonly monthlyModeValue = toSignal(this.recurrenceForm.controls.monthlyMode.valueChanges, {
    initialValue: this.recurrenceForm.controls.monthlyMode.value,
  });

  private readonly yearlyModeValue = toSignal(this.recurrenceForm.controls.yearlyMode.valueChanges, {
    initialValue: this.recurrenceForm.controls.yearlyMode.value,
  });

  private readonly rangeTypeValue = toSignal(this.recurrenceForm.controls.rangeType.valueChanges, {
    initialValue: this.recurrenceForm.controls.rangeType.value,
  });

  frequencies = computed<SelectOption<Frequency>[]>(() => {
    const supported = this.supportedPatternTypes();
    const options: SelectOption<Frequency>[] = [
      {
        label: this.translocoService.translate(translation.reservationPopup.recurrenceEditorMessages.frequenciesNever),
        value: 'never',
      },
    ];

    if (supported.includes('daily')) {
      options.push({
        label: this.translocoService.translate(translation.reservationPopup.recurrenceEditorMessages.frequenciesDaily),
        value: 'daily',
      });
    }
    if (supported.includes('weekly')) {
      options.push({
        label: this.translocoService.translate(translation.reservationPopup.recurrenceEditorMessages.frequenciesWeekly),
        value: 'weekly',
      });
    }
    if (supported.includes('absoluteMonthly') || supported.includes('relativeMonthly')) {
      options.push({
        label: this.translocoService.translate(
          translation.reservationPopup.recurrenceEditorMessages.frequenciesMonthly
        ),
        value: 'monthly',
      });
    }
    if (supported.includes('absoluteYearly') || supported.includes('relativeYearly')) {
      options.push({
        label: this.translocoService.translate(translation.reservationPopup.recurrenceEditorMessages.frequenciesYearly),
        value: 'yearly',
      });
    }

    return options;
  });

  weekIndexOptions = computed<SelectOption<CalendarRecurrenceWeekIndex>[]>(() =>
    WEEK_INDEXES.map((index) => ({
      value: index,
      label: this.translocoService.translate(this.weekIndexTranslationKey(index)),
    }))
  );

  monthOptions = computed<SelectOption<number>[]>(() => {
    const locale = this.resolveLocale();
    return Array.from({ length: 12 }, (_, i) => {
      const monthIndex = i + 1;
      return {
        value: monthIndex,
        label: new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2026, i, 1)),
      };
    });
  });

  dayOptions = computed<SelectOption<CalendarRecurrenceDayOfWeek>[]>(() =>
    DAYS_OF_WEEK.map((day) => ({
      value: day,
      label: this.translocoService.translate(this.weekdayTranslationKey(day)),
    }))
  );

  readonly isWeekly = computed(() => this.frequencyValue() === 'weekly');
  readonly isMonthly = computed(() => this.frequencyValue() === 'monthly');
  readonly isYearly = computed(() => this.frequencyValue() === 'yearly');
  readonly showPatternDetails = computed(() => this.frequencyValue() !== 'never');
  readonly isMonthlyAbsolute = computed(() => this.monthlyModeValue() === 'absolute');
  readonly isYearlyAbsolute = computed(() => this.yearlyModeValue() === 'absolute');
  readonly isRangeAfter = computed(() => this.rangeTypeValue() === 'after');
  readonly isRangeOn = computed(() => this.rangeTypeValue() === 'on');
  readonly startDayOfMonth = computed(() => this.start().getDate());
  readonly startMonthLabel = computed(() => this.capitalizeFirst(this.monthLabel(this.start().getMonth() + 1)));
  readonly startWeekdayLabel = computed(() =>
    this.capitalizeFirst(
      this.translocoService.translate(this.weekdayTranslationKey(this.jsDayToCalendarDay(this.start().getDay())))
    )
  );
  readonly startWeekIndexLabel = computed(() =>
    this.capitalizeFirst(
      this.translocoService.translate(this.weekIndexTranslationKey(this.weekOfMonthToIndex(this.start())))
    )
  );

  private onChange: (value: CalendarEventRecurrence | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      const isDisabled = this.disabled();
      if (isDisabled) {
        this.recurrenceForm.disable({ emitEvent: false });
      } else {
        this.recurrenceForm.enable({ emitEvent: false });
      }
    });

    effect(() => {
      // Keep the selected frequency compatible with provider capabilities.
      const supported = this.supportedPatternTypes();
      const current = this.recurrenceForm.controls.frequency.value;
      if (current === 'daily' && !supported.includes('daily')) {
        this.recurrenceForm.controls.frequency.setValue('never');
      }
      if (current === 'weekly' && !supported.includes('weekly')) {
        this.recurrenceForm.controls.frequency.setValue('never');
      }
      if (current === 'monthly' && !supported.some((x) => x === 'absoluteMonthly' || x === 'relativeMonthly')) {
        this.recurrenceForm.controls.frequency.setValue('never');
      }
      if (current === 'yearly' && !supported.some((x) => x === 'absoluteYearly' || x === 'relativeYearly')) {
        this.recurrenceForm.controls.frequency.setValue('never');
      }
    });

    effect(() => {
      // Recurrence semantics should always follow the edited event start date.
      const start = this.start();
      this.patchDefaultsFromStart(start, this.frequencyValue(), this.monthlyModeValue(), this.yearlyModeValue());
      this.emitValue();
    });
  }

  ngOnInit(): void {
    this.recurrenceForm.controls.rangeType.valueChanges.subscribe((rangeType) => {
      if (rangeType !== 'on') {
        return;
      }

      const maxEndDate = this.maxEndDate();
      if (maxEndDate) {
        this.recurrenceForm.controls.endDate.setValue(maxEndDate);
      }
    });

    this.recurrenceForm.valueChanges.subscribe(() => {
      this.emitValue();
    });
  }

  writeValue(value: CalendarEventRecurrence | null): void {
    this.suppressChange = true;
    this.recurrenceForm.patchValue(this.mapRecurrenceToForm(value), { emitEvent: false });
    this.suppressChange = false;
  }

  registerOnChange(fn: (value: CalendarEventRecurrence | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.recurrenceForm.disable({ emitEvent: false });
    } else {
      this.recurrenceForm.enable({ emitEvent: false });
    }
  }

  toggleWeekday(day: CalendarRecurrenceDayOfWeek): void {
    const current = this.recurrenceForm.controls.weeklyDays.value;
    const exists = current.includes(day);
    const next = exists ? current.filter((x) => x !== day) : [...current, day];
    this.recurrenceForm.controls.weeklyDays.setValue(next);
    this.onTouched();
  }

  isWeekdaySelected(day: CalendarRecurrenceDayOfWeek): boolean {
    return this.recurrenceForm.controls.weeklyDays.value.includes(day);
  }

  trackOption(_index: number, item: SelectOption<string | number>) {
    return item.value;
  }

  private emitValue() {
    if (this.suppressChange) {
      return;
    }
    this.onTouched();
    const recurrence = this.buildRecurrenceFromForm();
    this.onChange(recurrence);
  }

  private buildRecurrenceFromForm(): CalendarEventRecurrence | null {
    const frequency = this.recurrenceForm.controls.frequency.value;
    if (frequency === 'never') {
      return null;
    }

    const interval = this.clampNumber(this.recurrenceForm.controls.interval.value, 1, 999);
    const start = this.start();
    const startDate = this.formatDateOnly(start);
    const fallbackWeekday = this.jsDayToCalendarDay(start.getDay());
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    let pattern: CalendarEventRecurrence['pattern'];
    if (frequency === 'daily') {
      pattern = {
        type: 'daily',
        interval,
      };
    } else if (frequency === 'weekly') {
      const weeklyDays = this.recurrenceForm.controls.weeklyDays.value;
      pattern = {
        type: 'weekly',
        interval,
        daysOfWeek: weeklyDays.length > 0 ? weeklyDays : [fallbackWeekday],
      };
    } else if (frequency === 'monthly') {
      const preferredMode = this.recurrenceForm.controls.monthlyMode.value;
      const type =
        preferredMode === 'absolute'
          ? this.resolveSupportedMonthlyPattern('absoluteMonthly')
          : this.resolveSupportedMonthlyPattern('relativeMonthly');
      if (type === 'absoluteMonthly') {
        pattern = {
          type,
          interval,
          dayOfMonth: start.getDate(),
        };
      } else {
        pattern = {
          type,
          interval,
          index: this.weekOfMonthToIndex(start),
          daysOfWeek: [this.jsDayToCalendarDay(start.getDay())],
        };
      }
    } else {
      const preferredMode = this.recurrenceForm.controls.yearlyMode.value;
      const type =
        preferredMode === 'absolute'
          ? this.resolveSupportedYearlyPattern('absoluteYearly')
          : this.resolveSupportedYearlyPattern('relativeYearly');
      if (type === 'absoluteYearly') {
        pattern = {
          type,
          interval,
          month: start.getMonth() + 1,
          dayOfMonth: start.getDate(),
        };
      } else {
        pattern = {
          type,
          interval,
          month: start.getMonth() + 1,
          index: this.weekOfMonthToIndex(start),
          daysOfWeek: [this.jsDayToCalendarDay(start.getDay())],
        };
      }
    }

    const rangeType = this.recurrenceForm.controls.rangeType.value;
    let range: CalendarEventRecurrence['range'];
    if (rangeType === 'after') {
      range = {
        type: 'numbered',
        startDate,
        numberOfOccurrences: this.clampNumber(this.recurrenceForm.controls.occurrences.value, 1, 9999),
        recurrenceTimeZone: timezone,
      };
    } else if (rangeType === 'on') {
      const endDate = this.recurrenceForm.controls.endDate.value ?? start;
      range = {
        type: 'endDate',
        startDate,
        endDate: this.formatDateOnly(endDate),
        recurrenceTimeZone: timezone,
      };
    } else {
      range = {
        type: 'noEnd',
        startDate,
        recurrenceTimeZone: timezone,
      };
    }

    return {
      pattern,
      range,
    };
  }

  private mapRecurrenceToForm(value: CalendarEventRecurrence | null) {
    const start = this.start();
    const defaults = this.mapRecurrenceToFormDefaults(start);
    if (!value) {
      return defaults;
    }

    const frequency = this.patternTypeToFrequency(value.pattern.type);
    const monthlyMode: MonthlyMode = value.pattern.type === 'relativeMonthly' ? 'relative' : 'absolute';
    const yearlyMode: YearlyMode = value.pattern.type === 'relativeYearly' ? 'relative' : 'absolute';

    const rangeType: RangeType =
      value.range.type === 'numbered' ? 'after' : value.range.type === 'endDate' ? 'on' : ('never' as const);

    return {
      ...defaults,
      frequency,
      interval: value.pattern.interval ?? 1,
      weeklyDays: value.pattern.daysOfWeek?.length ? value.pattern.daysOfWeek : defaults.weeklyDays,
      monthlyMode,
      yearlyMode,
      dayOfMonth: value.pattern.dayOfMonth ?? defaults.dayOfMonth,
      month: value.pattern.month ?? defaults.month,
      weekIndex: value.pattern.index ?? defaults.weekIndex,
      weekDay: value.pattern.daysOfWeek?.[0] ?? defaults.weekDay,
      rangeType,
      occurrences: value.range.numberOfOccurrences ?? defaults.occurrences,
      endDate: value.range.endDate ? new Date(`${value.range.endDate}T00:00:00`) : defaults.endDate,
    };
  }

  private mapRecurrenceToFormDefaults(start: Date) {
    const day = start.getDate();
    const month = start.getMonth() + 1;
    const weekday = this.jsDayToCalendarDay(start.getDay());
    return {
      frequency: 'never' as Frequency,
      interval: 1,
      weeklyDays: [weekday] as CalendarRecurrenceDayOfWeek[],
      monthlyMode: 'absolute' as MonthlyMode,
      yearlyMode: 'absolute' as YearlyMode,
      dayOfMonth: day,
      month,
      weekIndex: this.weekOfMonthToIndex(start) as CalendarRecurrenceWeekIndex,
      weekDay: weekday,
      rangeType: 'on' as RangeType,
      occurrences: 10,
      endDate: this.maxEndDate() ?? start,
    };
  }

  private patchDefaultsFromStart(start: Date, frequency: Frequency, monthlyMode: MonthlyMode, yearlyMode: YearlyMode) {
    const controls = this.recurrenceForm.controls;
    const fallbackWeekday = this.jsDayToCalendarDay(start.getDay());
    const startWeekIndex = this.weekOfMonthToIndex(start);
    const startDayOfMonth = start.getDate();
    const startMonth = start.getMonth() + 1;

    if (controls.weeklyDays.value.length === 0) {
      controls.weeklyDays.setValue([fallbackWeekday], { emitEvent: false });
    }
    if (!controls.weeklyDays.value.includes(fallbackWeekday) && controls.frequency.value === 'weekly') {
      controls.weeklyDays.setValue([fallbackWeekday], { emitEvent: false });
    }
    if (!controls.weekDay.value) {
      controls.weekDay.setValue(fallbackWeekday, { emitEvent: false });
    }

    if (frequency === 'monthly') {
      if (monthlyMode === 'absolute') {
        controls.dayOfMonth.setValue(startDayOfMonth, { emitEvent: false });
      } else {
        controls.weekIndex.setValue(startWeekIndex, { emitEvent: false });
        controls.weekDay.setValue(fallbackWeekday, { emitEvent: false });
      }
    }

    if (frequency === 'yearly') {
      controls.month.setValue(startMonth, { emitEvent: false });
      if (yearlyMode === 'absolute') {
        controls.dayOfMonth.setValue(startDayOfMonth, { emitEvent: false });
      } else {
        controls.weekIndex.setValue(startWeekIndex, { emitEvent: false });
        controls.weekDay.setValue(fallbackWeekday, { emitEvent: false });
      }
    }
  }

  private resolveSupportedMonthlyPattern(
    preferred: 'absoluteMonthly' | 'relativeMonthly'
  ): 'absoluteMonthly' | 'relativeMonthly' {
    const supported = this.supportedPatternTypes();
    if (supported.includes(preferred)) {
      return preferred;
    }
    return preferred === 'absoluteMonthly' ? 'relativeMonthly' : 'absoluteMonthly';
  }

  private resolveSupportedYearlyPattern(
    preferred: 'absoluteYearly' | 'relativeYearly'
  ): 'absoluteYearly' | 'relativeYearly' {
    const supported = this.supportedPatternTypes();
    if (supported.includes(preferred)) {
      return preferred;
    }
    return preferred === 'absoluteYearly' ? 'relativeYearly' : 'absoluteYearly';
  }

  private patternTypeToFrequency(type: CalendarRecurrencePatternType): Frequency {
    if (type === 'daily') {
      return 'daily';
    }
    if (type === 'weekly') {
      return 'weekly';
    }
    if (type === 'absoluteMonthly' || type === 'relativeMonthly') {
      return 'monthly';
    }
    return 'yearly';
  }

  private clampNumber(value: number | string | null | undefined, min: number, max: number): number {
    const parsedValue = typeof value === 'string' ? Number.parseInt(value, 10) : value;
    if (parsedValue == null || Number.isNaN(parsedValue)) {
      return min;
    }
    return Math.min(max, Math.max(min, Math.floor(parsedValue)));
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private jsDayToCalendarDay(day: number): CalendarRecurrenceDayOfWeek {
    switch (day) {
      case 0:
        return 'sunday';
      case 1:
        return 'monday';
      case 2:
        return 'tuesday';
      case 3:
        return 'wednesday';
      case 4:
        return 'thursday';
      case 5:
        return 'friday';
      default:
        return 'saturday';
    }
  }

  private weekOfMonthToIndex(date: Date): CalendarRecurrenceWeekIndex {
    const week = Math.ceil(date.getDate() / 7);
    if (week <= 1) {
      return 'first';
    }
    if (week === 2) {
      return 'second';
    }
    if (week === 3) {
      return 'third';
    }
    if (week === 4) {
      return 'fourth';
    }
    return 'last';
  }

  private weekdayTranslationKey(day: CalendarRecurrenceDayOfWeek): string {
    return `workingHours.${day}`;
  }

  private weekIndexTranslationKey(index: CalendarRecurrenceWeekIndex): string {
    switch (index) {
      case 'first':
        return translation.reservationPopup.recurrenceEditorMessages.offsetPositionsFirst;
      case 'second':
        return translation.reservationPopup.recurrenceEditorMessages.offsetPositionsSecond;
      case 'third':
        return translation.reservationPopup.recurrenceEditorMessages.offsetPositionsThird;
      case 'fourth':
        return translation.reservationPopup.recurrenceEditorMessages.offsetPositionsFourth;
      default:
        return translation.reservationPopup.recurrenceEditorMessages.offsetPositionsLast;
    }
  }

  private resolveLocale() {
    const activeLang = this.translocoService.getActiveLang();
    if (activeLang === 'ua') {
      return 'uk';
    }
    return activeLang;
  }

  private monthLabel(monthIndex: number): string {
    const locale = this.resolveLocale();
    return new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2026, monthIndex - 1, 1));
  }

  private capitalizeFirst(value: string): string {
    if (!value) {
      return value;
    }
    return value.charAt(0).toLocaleUpperCase() + value.slice(1);
  }
}
